import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="privacy-page">
      <nav className="nav">
        <div className="nav-inner">
          <a
            href="/"
            className="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            <img src="/nutshell-logo-cropped.png" alt="Nutshell Video" className="nav-logo-img" />
            <span className="nav-logo-text">Nutshell</span>
          </a>
          <button onClick={onBack} className="nav-back">
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </nav>

      <motion.main
        className="privacy-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="privacy-inner">
          <h1>Privacy Policy</h1>
          <p className="privacy-updated">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <section>
            <h2>Overview</h2>
            <p>
              Nutshell Video (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your
              privacy. This Privacy Policy explains how our Chrome extension collects, uses, and safeguards your
              information.
            </p>
          </section>

          <section>
            <h2>Information We Collect</h2>
            <h3>Information You Provide</h3>
            <p>
              Nutshell Video does not require you to create an account or provide any personal information to use the
              extension.
            </p>

            <h3>Information Collected Automatically</h3>
            <p>When you use Nutshell Video, we collect:</p>
            <ul>
              <li>
                <strong>YouTube Video Data:</strong> Video transcripts, titles, and channel names from YouTube videos
                you choose to summarize. This data is sent to our servers solely for the purpose of generating
                summaries.
              </li>
              <li>
                <strong>Usage Data:</strong> Anonymous usage statistics such as the number of summaries generated, to
                help us improve our service.
              </li>
            </ul>

            <h3>Information We Do NOT Collect</h3>
            <ul>
              <li>Personal identification information (name, email, address)</li>
              <li>Browsing history outside of YouTube</li>
              <li>YouTube account credentials or login information</li>
              <li>Any data from websites other than YouTube</li>
            </ul>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Generate AI-powered summaries of YouTube videos</li>
              <li>Improve and optimize our summarization algorithms</li>
              <li>Monitor and maintain the performance of our service</li>
              <li>Detect and prevent abuse or misuse of the service</li>
            </ul>
          </section>

          <section>
            <h2>Data Storage and Security</h2>
            <ul>
              <li>
                <strong>Local Storage:</strong> The extension stores your preferences (such as summary length) and
                cached transcripts locally in your browser. This data never leaves your device unless you request a
                summary.
              </li>
              <li>
                <strong>Server Processing:</strong> When you request a summary, the video transcript is sent to our
                secure servers for processing. We do not permanently store transcripts or summaries on our servers.
              </li>
              <li>
                <strong>Encryption:</strong> All data transmitted between the extension and our servers is encrypted
                using HTTPS.
              </li>
            </ul>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <p>
              We use Anthropic&apos;s Claude AI to generate summaries. Video transcripts are sent to Anthropic&apos;s
              API for processing. Please refer to{' '}
              <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">
                Anthropic&apos;s Privacy Policy
              </a>{' '}
              for information on how they handle data.
            </p>
          </section>

          <section>
            <h2>Data Retention</h2>
            <p>
              We do not permanently store your video transcripts or generated summaries. Transcript data is processed in
              real-time and discarded after the summary is generated and delivered to you.
            </p>
          </section>

          <section>
            <h2>Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Clear locally stored data by removing the extension or clearing your browser data</li>
              <li>Choose not to use the service at any time</li>
              <li>Request information about what data we process</li>
            </ul>
          </section>

          <section>
            <h2>Children&apos;s Privacy</h2>
            <p>
              Nutshell Video is not intended for children under 13 years of age. We do not knowingly collect personal
              information from children under 13.
            </p>
          </section>

          <section>
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify users of any material changes by
              updating the &quot;Last updated&quot; date at the top of this policy.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us through the Chrome Web Store
              listing.
            </p>
          </section>
        </div>
      </motion.main>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/nutshell-logo-cropped.png" alt="Nutshell Video" className="footer-logo" />
            <span className="footer-name">Nutshell Video</span>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Nutshell Video. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
